import React from "react"
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
} from "@mui/material"

import { Row, Col } from "react-bootstrap"

import PublicIcon from "@mui/icons-material/Public"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import { motion } from "framer-motion"

import ContactForm from "../components/ContactForm"
import CallToAction from '../components/CallToAction'

const stats = [
  { label: "Clients", value: "2,345" },
  { label: "Staff Members", value: "345" },
  { label: "Services Offered", value: "120+" },
  { label: "Countries Served", value: "14+" },
]

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const ServicePage = () => {
  return (
    <Box sx={{ bgcolor: "#f4f6f8" }}>
      {/* HERO SECTION */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        style={{ background: "#F5F5F5", minHeight: "60vh", display: "flex", alignItems: "center", textAlign: "center", padding: "0 16px" }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={700} gutterBottom>
            Reliable Accounting Services
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={4}>
            We provide tailored accounting services to help your business grow. Let us handle the numbers so you can focus on what matters.
          </Typography>
          <Button variant="contained" size="large" sx={{ px: 5, py: 1.5, borderRadius: 5 }} whileHover={{ scale: 1.05 }}>
            Get Started
          </Button>
        </Container>
      </motion.section>

      {/* QUOTE + FORM */}
      <Box component="section" py={8}>
        <Container maxWidth="lg">
          <Row container spacing={6}>
            <Col item xs={12} md={7}>
              <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Get a Free Quote Calculation
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                  For a quick calculation of your expenses, send us your info. Our accounting experts will build the best possible offer.
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <PublicIcon color="primary" sx={{ fontSize: 48 }} />
                    <Typography variant="h6" fontWeight={600} mt={2}>
                      International Accounting
                    </Typography>
                    <Typography color="text.secondary">
                      Operating in over 14 countries with 200+ employees worldwide.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SupportAgentIcon color="primary" sx={{ fontSize: 48 }} />
                    <Typography variant="h6" fontWeight={600} mt={2}>
                      24/7 Availability
                    </Typography>
                    <Typography color="text.secondary">
                      Always available with fast response and delivery times.
                    </Typography>
                  </Grid>
                </Grid>
              </motion.div>
            </Col>

            <Col item xs={12} md={5}>
              <motion.div whileHover={{ scale: 1.02 }} style={{ padding: 32, border: "1px solid #eee", backgroundColor: "#fff", borderRadius: 8 }}>
                <ContactForm />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </Box>

      {/* STATS */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        style={{
          color: "white",
          backgroundImage: "url('/servicepage.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          textAlign: "center",
          padding: "80px 0",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} mb={2}>
            Our Experience & Expertise
          </Typography>
          <Typography mb={6}>
            We bring the best possible solutions for your business growth.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {stats.map((item) => (
              <Grid item xs={6} md={3} key={item.label}>
                <Typography variant="h4" fontWeight={700}>
                  {item.value}
                </Typography>
                <Typography>{item.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </motion.section>

      {/* ABOUT */}
      <motion.section initial="hidden" whileInView="visible" variants={fadeUp} viewport={{ once: true }} style={{ padding: "64px 0", backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                About Us
              </Typography>
              <Typography variant="h6" color="text.secondary" mb={3}>
                A professional accounting team with global experience.
              </Typography>
              <Typography color="text.secondary" mb={2}>
                We specialize in business management consulting, helping organizations grow sustainably.
              </Typography>
              <Typography color="text.secondary">
                We’ve worked with Global 500 companies, nonprofits, and private equity funds representing 75% of global equity capital.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </motion.section>

      {/* NEWS */}
      <motion.section initial="hidden" whileInView="visible" variants={fadeUp} viewport={{ once: true }} style={{ padding: "64px 0" }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            Latest Articles
          </Typography>
          <Typography align="center" color="text.secondary" mb={6}>
            Strategic insights and practical actions for real results.
          </Typography>
          <Row container spacing={4}>
            {["EU VAT Changes", "Financial Reports", "Accounting Basics"].map((title, i) => (
              <Col item xs={12} md={4} key={i}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, transition: "0.3s" }}>
                    <Typography variant="h6" fontWeight={600}>
                      {title}
                    </Typography>
                    <Typography color="text.secondary" my={2}>
                      December 12, 2014
                    </Typography>
                    <Typography color="text.secondary" mb={2}>
                      Learn how these updates affect your business accounting.
                    </Typography>
                    <Button size="small">Read More</Button>
                  </Paper>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.section>

      {/* FINAL CTA */}
      <CallToAction></CallToAction>
    </Box>
  )
}

export default ServicePage
